import React, { useEffect, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_HIGHLY_SENSITIVE_PERSON,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import SendToFriendButton from "../../components/send-to-friend-button";
import ShareButton from "../../components/share-button";
import {
  getCookie,
  getEnneagramBannerImage,
  getHighlySensitiveReportPageImage,
} from "../../utils/utility-functions";
import ExpandableSection from "../../components/expandable-collapsible/component";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

const HighlySensitivePersonReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();

  const [contentData, setContentData] = useState({});
  const { t } = useTranslation();
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_HIGHLY_SENSITIVE_PERSON[currentLocale] ||
            MODEL_TYPE_HIGHLY_SENSITIVE_PERSON[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const bannerImage = getEnneagramBannerImage();
  const result1ImageText = "highly-sensitive-1";
  const result2ImageText = "highly-sensitive-2";
  const result1Image = getHighlySensitiveReportPageImage(result1ImageText);
  const result2Image = getHighlySensitiveReportPageImage(result2ImageText);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
    pink: "from-pink-600 via-pink-500",
  };

  const formatSourceContent = (content) => {
    if (typeof content === "string") {
      if (content.startsWith("http://") || content.startsWith("https://")) {
        return (
          <a
            href={content}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#805fe6]"
          >
            {content}
          </a>
        );
      }
      return content;
    }
    return content;
  };

  const TakeTestButton = ({ onClick }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

  const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full md:h-full object-cover object-right"
        />

        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Content */}
        <div className="relative h-full flex items-center">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            {/* Title & Button Wrapper */}
            <div className="flex flex-col  md:flex-row items-center md:items-start justify-between gap-4 pr-10 md:pr-16">
              {/* Title */}
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
              >
                {title}
              </h2>

              {/* Button (if needed) */}
              {buttonNeeded && (
                <div className="flex-shrink-0">
                  <TakeTestButton onClick={onClick} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CallToActionSection = ({ onClick1, onClick2 }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.testMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.discoverMessage")}
            </p>
            <TakeTestButton onClick={onClick1} />
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.adhd.bigFiveMessage")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.adhd.strengthMesssge")}
            </p>
            <TakeTestButton onClick={onClick2} />
          </div>
        </div>
      </section>
    );
  };

  const InfoListCard = ({ heading, bgClr, contentArray, overview }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col gap-3 sm:gap-4 h-full"
      >
        {/* Heading */}
        {heading && (
          <h2 className="text-lg font-semibold text-gray-700">{heading}</h2>
        )}

        {/* Overview (Avoiding Extra White Space) */}
        {overview && (
          <p className="text-base text-gray-700 flex-1">{overview}</p>
        )}

        {/* Content List */}
        {contentArray && (
          <ul className="space-y-2 pl-1">
            {contentArray.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="flex items-start gap-2">
                  {/* Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] lg:mt-[3px] md:mt-[3px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Text Content */}
                  <p className="flex-1 text-gray-700 text-sm sm:text-base leading-tight">
                    {item?.Heading ? (
                      <div>
                        <strong>{item.Heading}</strong>
                        <div>{formatSourceContent(item.Content)}</div>
                      </div>
                    ) : (
                      <div>{formatSourceContent(item)}</div>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const EyeIcon = ({ size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;
    const minSize = 16;
    const actualSize = Math.max(size, minSize);

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        {/* Purple eye background */}
        <circle cx={centerPoint} cy={centerPoint} r={38} fill={color} />

        {/* White eye shape */}
        <path
          d="M50 35 C65 35, 80 45, 80 50 C80 55, 65 65, 50 65 C35 65, 20 55, 20 50 C20 45, 35 35, 50 35 Z"
          fill="white"
        />

        {/* Center pupil */}
        <circle cx={centerPoint} cy={centerPoint} r={10} fill={color} />
      </svg>
    );
  };

  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;
    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor = isNumber ? "#a98ff9" : "#805bec";
    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
      >
        {/* Inner Purple Circle */}
        <circle cx={centerPoint} cy={centerPoint} r={40} fill={finalColor} />

        {/* Character/Text inside the Circle */}
        {!isBullet && (
          <text
            x={centerPoint}
            y={centerPoint + 4}
            fontSize={textSize}
            fontWeight="bold"
            fill="white"
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="Arial, sans-serif"
          >
            {character}
          </text>
        )}
      </svg>
    );
  };

  const Table = ({ YourScore }) => (
    <div className="w-full bg-white">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-300">
            <th className="p-3 text-left font-bold tracking-wider text-lg">
              {t("report.highly_sensitive_person.your_score")}
            </th>
            <th className="p-3 text-right font-bold tracking-wider text-lg text-[#805bec]">
              {YourScore}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          <tr className="border-b border-gray-300"></tr>
        </tbody>
      </table>
    </div>
  );

  const TipsCard = ({ tips }) => {
    return (
      <div
        style={{ backgroundColor: "#eef4fe" }}
        className="bg-gray-100 p-4 md:p-6 rounded-2xl shadow-lg w-full max-w-2xl"
      >
        {/* Main Heading with responsive icon sizing */}

        <div className="flex items-start gap-2 mb-8">
          <div>
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 shrink-0 flex items-start justify-center rounded-full border-1 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                <CircleIcon character={"i"} size={30} />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                {tips?.RootHeading}
              </h2>
            </div>
          </div>
        </div>

        <ul className="my-1 sm:my-2 pl-6 sm:pl-10 space-y-3 sm:space-y-4">
          {tips?.Content?.map((section, index) => (
            <li key={index} className="text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <div>
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] lg:mt-[5px] md:mt-[5px]">
                      <CircleIcon
                        character={(index + 1).toString()}
                        size={16}
                      />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 leading-none">
                      {section?.Heading}
                    </h3>
                  </div>

                  {/* Content aligned to Heading */}
                  <p className="pl-7 text-base text-gray-700">
                    {section?.Content}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const KeyInsightsCard = ({ keyInsight }) => {
    return (
      <div
        style={{ backgroundColor: "#fdf6eb" }}
        className="bg-gray-100 p-4 md:p-6 rounded-2xl shadow-lg w-full max-w-2xl"
      >
        {/* Main Heading with responsive icon sizing */}

        <div className="flex items-start gap-2 mb-8">
          <div>
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 shrink-0 flex items-start justify-center rounded-full border-1 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                <EyeIcon size={30} />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                {keyInsight?.RootHeading}
              </h2>
            </div>
          </div>
        </div>

        <ul className="my-1 sm:my-2 pl-6 sm:pl-10 space-y-3 sm:space-y-4">
          {keyInsight?.Content?.map((section, index) => (
            <li key={index} className="text-sm text-gray-600">
              {/* Heading with DiscSVG */}
              <div className="flex items-start gap-2">
                <div>
                  <div className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] lg:mt-[5px] md:mt-[5px]">
                      <CircleIcon character={""} size={16} />
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-700 leading-none">
                      {section?.Heading}
                    </h3>
                  </div>

                  {/* Content aligned to Heading */}
                  <p className="pl-7 text-base text-gray-700">
                    {section?.Content}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  function transformUserTraitsData(data) {
    if (!Array.isArray(data)) return { result: {}, totalScore: 0 };

    const result = data.reduce((acc, item) => {
      if (
        item?.trait?.title &&
        typeof item.percentage === "number" &&
        typeof item.score === "number"
      ) {
        acc[item.trait.title] = {
          percentage: item.percentage,
          score: item.score,
        };
      }
      return acc;
    }, {});

    const totalScore = data.reduce((sum, item) => sum + (item?.score || 0), 0);

    return { result, totalScore };
  }

  function getOverallSensitivityDetails(interpretation, score) {
    if (!interpretation || !score) return null;

    for (let i = 0; i < interpretation.ColumnA.length; i++) {
      const [min, max] = interpretation.ColumnA[i].split("-").map(Number);
      if (score >= min && score <= max) {
        const sensitivityObj = interpretation.ColumnB[i];
        return {
          Range: interpretation.ColumnA[i],
          Sensitivity: sensitivityObj.EnglishCode, // or use .text if you want the localized version
          SensitivityText: sensitivityObj.text, // optional: for multilingual use
          Content: interpretation.ColumnC[i],
        };
      }
    }

    return null;
  }

  function getRecommendations(recommendations, sensitivityLevel) {
    if (!recommendations || !sensitivityLevel) return null;

    return (
      recommendations.Content.find((item) =>
        item.EnglishCode.includes(sensitivityLevel.split(" ")[0])
      ) || null
    );
  }

  const interpretation = contentData?.interpretation;

  const recommendations = contentData?.recommendations;

  const updatedUserTraits = transformUserTraitsData(userTraits);

  const totalScore = updatedUserTraits?.totalScore;

  const resources = contentData?.resources;

  const overallSensitivityDetails = getOverallSensitivityDetails(
    interpretation,
    totalScore
  );

  const filteredRecommendations = getRecommendations(
    recommendations,
    overallSensitivityDetails?.Sensitivity
  );

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-6 items-stretch">
                        {/* Left Section - Text & Table */}
                        <div className="flex flex-col h-full">
                          <h2 className="text-lg font-semibold text-gray-700 mb-4">
                            {t("report.highly_sensitive_person.introduction")}
                          </h2>
                          <p className="text-gray-700 whitespace-pre-line">
                            {contentData?.intro}
                          </p>

                          {/* Table */}
                          <div className="lg:mt-12 md:mt-12 mt-6">
                            <Table YourScore={totalScore} />
                          </div>
                        </div>

                        {/* Right Section - Image */}
                        <div className="flex items-center justify-center h-full">
                          <img
                            src={result1Image}
                            alt="BannerImage"
                            className="w-full max-h-[500px] object-cover"
                          />
                        </div>
                      </div>
                      <h2 className="text-lg font-semibold text-gray-700 mb-4 mt-4">
                        {contentData?.interpretation?.RootHeading}
                      </h2>

                      <DescriptiveCard
                        heading={overallSensitivityDetails?.SensitivityText}
                        overview={overallSensitivityDetails?.Content}
                        bgClr={"bg-blue-50"}
                        textClr={"text-blue-800"}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.understandingSensitivity?.RootHeading}
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="bg-white">
                      <p class="text-gray-700 whitespace-pre-line">
                        {contentData?.understandingSensitivity?.FlavorText}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4 mb-6 items-stretch">
                        <KeyInsightsCard
                          keyInsight={{
                            RootHeading: t(
                              "report.highly_sensitive_person.key_insights"
                            ),
                            Content:
                              contentData?.understandingSensitivity?.Content,
                          }}
                        />
                        <TipsCard tips={contentData?.tips} />
                      </div>
                      <InfoListCard
                        heading={
                          t("report.highly_sensitive_person.recommendations") +
                          " " +
                          filteredRecommendations?.Heading
                        }
                        contentArray={filteredRecommendations?.Content}
                        bgClr={"#ffeded"}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={t(
                        "report.highly_sensitive_person.resources_and_suggestions"
                      )}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-[3fr_1.5fr] gap-6 my-4 items-stretch mb-6">
                      {/* Left Section - Info Card */}
                      <InfoListCard
                        heading={resources?.RootHeading}
                        bgClr="#F7F3FE" // Soft purple background
                        contentArray={resources?.Content}
                      />

                      {/* Right Section - Image */}
                      <div className="flex items-center justify-center h-full">
                        <img
                          src={result2Image}
                          alt="BannerImage"
                          className="w-full max-h-[500px] object-cover"
                        />
                      </div>
                    </div>

                    <DescriptiveCard
                      heading={t("report.highly_sensitive_person.disclaimer")}
                      overview={contentData?.conclusion}
                      bgClr={"bg-green-50"}
                      textClr={"text-green-800"}
                    />
                  </div>
                </ExpandableSection>
              </section>
              <CallToActionSection
                onClick1={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
                onClick2={() => {
                  navigate(
                    `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                  );
                }}
              />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default HighlySensitivePersonReport;
